import { ReactNode, useMemo, useCallback } from 'react';

import { Resp, useApi } from '#/api/fetch-api';
import { createOrder, CreateOrderReq, Order, OrderSide } from '#/api/orders';

import createSafeContext from '#/utils/createSafeContext';

import {
  MarketView,
  usePerpetualMarketsStoreView,
  usePerpetualMarketsView,
} from '#/features/perpetuals/market-info/markets-context';
import { signOrderRequest } from '#/features/order-signature';
import { publishNotification } from '#/features/notifications/topic';
import { useSystemConfig } from '#/features/system/system-config-context';

import { useOrderFormView } from './form-context';
import { OrderFormView } from './form-view';

interface Success {
  readonly ok: true;
}

interface Failure {
  readonly ok: false;
  readonly message: string;
  readonly error: Error;
  readonly status: number;
}

export interface RespCallback {
  (resp: Success | Failure): void;
}

interface OrderSubmitContext {
  readonly isLoading: boolean;
  readonly submit: (side: OrderSide, cb?: RespCallback) => void;
}

export const [useOrderSubmit, SubmitCtx] =
  createSafeContext<OrderSubmitContext>('OrderSubmitContext');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OrderSubmitProvider({ children }: ProviderProps) {
  const [{ isLoading }, exec] = useApi(createOrder);
  const { activeMarket } = usePerpetualMarketsView();
  const formView = useOrderFormView();
  const handleNotifications = useHandleNotifications();
  const config = useSystemConfig();

  const submit = useCallback<OrderSubmitContext['submit']>(
    (side, cb?) => {
      if (!formView.isValid) throw new TypeError(INVALID_ERROR_MESSAGE);

      exec(
        prepareRequest(activeMarket!, side, formView, config.starknet.chainId),
        (resp) => {
          if (resp.ok) {
            handleNotifications(resp.data);
          }
          cb?.(prepareResponse(resp));
        },
      );
    },
    [
      activeMarket,
      config.starknet.chainId,
      exec,
      formView,
      handleNotifications,
    ],
  );

  const value = useMemo(() => ({ isLoading, submit }), [isLoading, submit]);

  return <SubmitCtx value={value}>{children}</SubmitCtx>;
}

function prepareRequest(
  activeMarket: MarketView,
  side: OrderSide,
  formView: OrderFormView,
  starknetChainId: string,
): CreateOrderReq {
  const request = {
    signature_timestamp: Date.now(),
    market: activeMarket.symbol,
    side,
    type: formView.type,
    size: formView.amountBaseAsset.value,
    price: formView.limitPrice.value,
    instruction: formView.instruction,
  };
  return signOrderRequest(request, starknetChainId);
}

function prepareResponse(resp: Resp<unknown>): Success | Failure {
  return resp.ok
    ? { ok: true }
    : {
        ok: false,
        message: resp.message,
        error: resp.error,
        status: resp.status,
      };
}

function useHandleNotifications() {
  const { getMarket } = usePerpetualMarketsStoreView();

  return useCallback(
    (order: Order) => {
      const market = getMarket(order.market);
      if (market == null) return;

      publishNotification({
        kind: 'submitted-order',
        props: { order, market },
      });
    },
    [getMarket],
  );
}

const INVALID_ERROR_MESSAGE =
  'OrderFormState is not valid. Make sure to only call this function when the state is valid.';
